import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useCADQuery } from '@/services/queries/cads'
import { useUpdateCadVersionMutation } from '../../queries'

import type { ASMTreeNode, RawAssemblyTree } from '@/state'

export const RenameTreeNodeInput = ({
  node,
  onRename,
  isEditing,
  tree,
}: {
  node: ASMTreeNode
  onRename: () => void
  isEditing: boolean
  tree: RawAssemblyTree
}) => {
  const [newName, setNewName] = useState(node.display_name)

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current && isEditing) {
      window.requestAnimationFrame(() => {
        inputRef?.current?.focus()
      })
    }
  }, [inputRef, isEditing])

  const { data: cadData, refetchCadVersion, isLoading } = useCADQuery()
  const { mutate: updateCadVersion, isPending: isSaving } =
    useUpdateCadVersionMutation({
      onSettled: () => {
        onRename()
      },
    })

  const cadVersionId = useMemo(
    () => cadData?.version?.id as string,
    [cadData?.version],
  )
  const documentVersionId = useMemo(
    () => cadData?.documentVersion?.id as string,
    [cadData?.documentVersion],
  )

  const handlePartRename = useCallback(
    (partName: string) => {
      if (!tree) return
      const assemblyTree = { ...tree }
      const partNode = assemblyTree.nodes.find((n) => n.uuid === node.uuid)
      if (!partNode) return
      partNode.display_name = partName

      if (assemblyTree && cadVersionId && documentVersionId) {
        updateCadVersion({
          cadVersionId,
          documentVersionId,
          assemblyTree: tree,
        })
        refetchCadVersion()
      }
    },
    [
      tree,
      node,
      cadVersionId,
      documentVersionId,
      updateCadVersion,
      refetchCadVersion,
    ],
  )

  if (isLoading || isSaving) {
    return <Skeleton className="h-6 w-full" />
  }

  return (
    <Input
      ref={inputRef}
      value={newName}
      className="h-auto p-1"
      onBlur={() => handlePartRename(newName)}
      onChange={(e) => {
        setNewName(e.target.value)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          e.currentTarget.blur()
        }
      }}
    />
  )
}
